"use client";

import { useEffect, useRef, useState, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import LibrarySeal from "@/components/ui/LibrarySeal";
import type { MagicSupplyStatus } from "@/lib/magic-supply";
import { fillUiText, type UiTextKey } from "@/lib/ui-texts";
import { useUiTexts } from "@/lib/ui-texts-client";

/** 板の「あと N冊 で LvN」の中で大きい字にする差し込み */
const BIG_TOKEN = "{あと冊数}";
/** 欄と板の文の名前（台帳の level.* の後ろの部分） */
type LevelTextKey = { [K in UiTextKey]: K extends `level.${infer R}` ? R : never }[UiTextKey];

interface Props {
  supply: MagicSupplyStatus;
  /** 無償の魔力（ためておける上限は無償の分にかかる） */
  freePoints: number;
  /** 利用者カードの帯に出すユーザーネーム */
  userName: string;
  /** 押す前のボタンの右に出す魔力（10/12 からホームの上の帯の「魔力：895」をここへ取り込んだ）。paid は内訳の読み上げに使う */
  magic: { free: number; paid: number };
  /** カードの裏の「これまでの記録」。分からない数（読み込む前・古いアカウント）は null で、その行を出さない */
  records: {
    /** 書庫の本（未読も含む） */
    shelfBooks: number | null;
    /** 持っている精霊（解放した精霊は数えない） */
    ownedSpirits: number | null;
    /** 図書館に来た日（ログイン報酬の累計の日数） */
    loginDays: number | null;
    /** 登録日（API から文字列で届く） */
    createdAt: Date | string | null;
  };
}

/** 登録日を「2026.9.24」の形に。読めない値なら null */
function joinedText(value: Date | string | null): string | null {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return `${d.getFullYear()}.${d.getMonth() + 1}.${d.getDate()}`;
}

/** そのレベルの中での進み。最後のレベルなら null */
function levelProgress(supply: MagicSupplyStatus): { done: number; need: number } | null {
  if (supply.nextReads === null) return null;
  // levelReads は後から足した値。足す前の返事が端末に残っていても崩れないよう 0 で受ける
  const base = supply.levelReads ?? 0;
  const need = Math.max(1, supply.nextReads - base);
  const done = Math.min(need, Math.max(0, supply.uniqueReads - base));
  return { done, need };
}

function untilText(ms: number): string {
  const minutes = Math.max(1, Math.ceil(ms / 60000));
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m}分`;
  return m === 0 ? `${h}時間` : `${h}時間${m}分`;
}

/** 次の魔力が届くまでの残り（板を開いたときの時刻で数える。前から描くたびに数え直している） */
function untilNextSupply(supply: MagicSupplyStatus): string {
  return untilText(supply.nextSupplyAt - Date.now());
}

function Gauge({
  ratio,
  className,
  track = "bg-white/15",
  bar = "from-violet-400 to-amber-300",
}: {
  ratio: number;
  className: string;
  /** 溝の地 */
  track?: string;
  /** 進んだ分の色（左から右へのぼかし） */
  bar?: string;
}) {
  return (
    <span className={`block overflow-hidden rounded-full ${track} ${className}`}>
      <span
        className={`block h-full rounded-full bg-gradient-to-r ${bar}`}
        style={{ width: `${Math.round(ratio * 100)}%` }}
      />
    </span>
  );
}

/** 利用者カードの紙（生成りの厚紙に細かい布目） */
const CARD_PAPER: CSSProperties = {
  backgroundColor: "#f3ead6",
  backgroundImage:
    "repeating-linear-gradient(0deg, #2b221908 0 1px, transparent 1px 3px), repeating-linear-gradient(90deg, #2b221906 0 1px, transparent 1px 3px), linear-gradient(160deg, #f8f1e1, #e8d9ba)",
};
/** 表と裏で同じ。重ねて置き、向こう側を向いた面は見せない。縦横は図書館の利用者カード（85.6×54mm）と同じ比で、中身が多ければ縦に伸びる
 * （min-w-0 と grid-cols-1 が無いと、伸びた高さに比を合わせて横にも広がり、画面からはみ出す） */
const CARD_FACE =
  "flex min-w-0 aspect-[1.586/1] flex-col rounded-[14px] text-[#2b2219] shadow-xl shadow-black/60 ring-1 ring-inset ring-[#b8924f]/50 [grid-area:1/1] [backface-visibility:hidden] [-webkit-backface-visibility:hidden]";

/**
 * ホームの左の列（館の札の下）のレベル: 「Lv2 12/100冊」とゲージ（そのレベルの中で、初めて読み終えた冊数の進み）。
 * 押すと、図書館の利用者カードを出す。表はレベルと魔力（あと何冊で上がるか・毎日届く量・ためておける上限）、押して裏返すとこれまでの記録（読み終えた本・書庫の本・精霊・来た日・登録日）。
 * 板は document.body の直下へ出す。親のすりガラス（backdrop-filter）が iPhone の Chrome で fixed をその箱の中に閉じ込めるため（館の札と同じ欄の中にいる）
 */
export default function UserLevelBadge({ supply, freePoints, userName, records, magic }: Props) {
  const [open, setOpen] = useState(false);
  /** カードの裏（魔力）を向けているか。開くたびに表から */
  const [flipped, setFlipped] = useState(false);
  // 文は管理UIの「文言」で書き換えられる（src/lib/ui-texts.ts の level.*）
  const { raw } = useUiTexts();
  const [mounted, setMounted] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  /** 板を閉じた一押しが、下の「本を引く」などを押してしまわないよう、続く click を1回止める */
  const suppressClickRef = useRef(false);

  useEffect(() => setMounted(true), []);

  // 板の外に指が触れたら閉じる（幕で受け止める形は iPhone で効かないことがあったので、読書画面の設定の板と同じ形）
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node;
      if (panelRef.current?.contains(target) || buttonRef.current?.contains(target)) return;
      setOpen(false);
      suppressClickRef.current = true;
      window.setTimeout(() => {
        suppressClickRef.current = false;
      }, 600);
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    return () => document.removeEventListener("pointerdown", onPointerDown, true);
  }, [open]);
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (!suppressClickRef.current) return;
      suppressClickRef.current = false;
      e.preventDefault();
      e.stopPropagation();
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, []);

  const progress = levelProgress(supply);
  const ratio = progress ? progress.done / progress.need : 1;
  const full = freePoints >= supply.cap;
  const left = progress ? progress.need - progress.done : 0;
  /** 欄と板の文の差し込みに入る数字 */
  const vars: Record<string, string | number> = {
    レベル: supply.level,
    次のレベル: supply.level + 1,
    読んだ: progress?.done ?? 0,
    要る: progress?.need ?? 0,
    あと: left,
    冊数: supply.uniqueReads,
    時: supply.dayStartHour,
    量: supply.dailyAmount,
    上限: supply.cap,
    次の上限: supply.nextCap ?? "",
    残り時間: untilNextSupply(supply),
    名前: userName,
    書庫: records.shelfBooks ?? "",
    精霊: records.ownedSpirits ?? "",
    来た日数: records.loginDays ?? "",
    登録日: joinedText(records.createdAt) ?? "",
  };
  /** 欄と板の文（管理UIで書き換えていればその文）。差し込み（{レベル} など）に数字を入れる */
  const t = (key: LevelTextKey) => fillUiText(raw(`level.${key}`), vars);
  /** カードの裏の行。数が分からない行と、管理UIで名前を空にした行は出さない */
  const recordRows = (
    [
      { id: "reads", known: true, label: t("readsLabel"), value: t("readsValue") },
      { id: "shelf", known: records.shelfBooks !== null, label: t("shelfLabel"), value: t("shelfValue") },
      { id: "spirits", known: records.ownedSpirits !== null, label: t("spiritsLabel"), value: t("spiritsValue") },
      { id: "days", known: records.loginDays !== null, label: t("daysLabel"), value: t("daysValue") },
      { id: "joined", known: joinedText(records.createdAt) !== null, label: t("joinedLabel"), value: t("joinedValue") },
    ] as const
  ).filter((row) => row.known && row.label);
  // 「あと N冊 で LvN」は {あと冊数} のところだけ大きい字にする
  const [bigBefore, bigAfter] = (() => {
    const template = raw("level.bigNext");
    const i = template.indexOf(BIG_TOKEN);
    if (i < 0) return [fillUiText(template, vars), null];
    return [fillUiText(template.slice(0, i), vars), fillUiText(template.slice(i + BIG_TOKEN.length), vars)];
  })();

  const panel = (
    <div className="pointer-events-none fixed inset-0 z-50 mx-auto flex max-w-md items-start justify-center bg-black/40 px-4 pt-16">
      <div
        ref={panelRef}
        role="dialog"
        aria-label={`${t("heading")} ${t("boardLevel")}`}
        className="pointer-events-auto w-full"
      >
        {/* × はカードの外の上に置く（カードと一緒に回らないように） */}
        <div className="mb-2 flex justify-end">
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label={t("close")}
            className="grid h-8 w-8 place-items-center rounded-full bg-black/45 text-lg leading-none text-[#f3dca4]/80 ring-1 ring-[#b8924f]/40 hover:text-[#f3dca4]"
          >
            ×
          </button>
        </div>

        {/* 利用者カード。どこを押しても裏返る（右下の字は、キーボードで裏返すための押し場所も兼ねる） */}
        <div className="[perspective:1400px]">
          <div
            onClick={() => setFlipped((v) => !v)}
            className="grid grid-cols-1 cursor-pointer select-none transition-transform duration-700 ease-[cubic-bezier(.2,.7,.2,1)] [transform-style:preserve-3d] motion-reduce:transition-none"
            style={{ transform: flipped ? "rotateY(180deg)" : "none" }}
          >
            {/* 表: レベル */}
            <div className={CARD_FACE} style={CARD_PAPER} aria-hidden={flipped}>
              {/* 帯（下のナビの紺の革と同じ色に、金の箔押し） */}
              <div className="flex items-center gap-2 rounded-t-[14px] bg-gradient-to-br from-[#2b3a58] to-[#151d31] px-4 py-2 shadow-[inset_0_-1px_0_#b8924f80]">
                <span aria-hidden="true" className="flex">
                  <LibrarySeal text="" emblem="book" size={26} />
                </span>
                <span className="bg-gradient-to-b from-[#f3dca4] via-[#d9b56f] to-[#9c7638] bg-clip-text font-mincho text-[15px] font-semibold tracking-[.16em] text-transparent">
                  {t("cardBrand")}
                </span>
                {/* 名前とレベル（カードを利用者カードの大きさに近づけるため、レベルは帯の中に置く。「あなたのレベル」は読み上げの名前だけ） */}
                <span className="ml-auto flex min-w-0 items-baseline gap-2">
                  <span className="min-w-0 truncate font-mincho text-[11px] tracking-[.2em] text-[#e9d6a8]/85">{t("cardTitle")}</span>
                  <span className="shrink-0 bg-gradient-to-b from-[#f3dca4] via-[#d9b56f] to-[#9c7638] bg-clip-text font-mincho text-lg font-bold leading-none text-transparent">
                    {t("boardLevel")}
                  </span>
                </span>
              </div>

              <div className="flex flex-1 flex-col px-4 pb-3 pt-2.5">
                {/* 魔力（レベルで上限が変わるので表に置く）。文で説明すると変な所で折れるので、名前と数字の行にそろえる */}
                <div className="border-b border-[#2b2219]/15 pb-2 text-sm">
                  <p className="mb-1 font-mincho text-xs font-semibold">{t("magicHeading")}</p>
                  <dl className="flex flex-col gap-0.5">
                    <div className="flex items-baseline justify-between gap-3">
                      <dt className="opacity-70">{t("dailyLabel")}</dt>
                      <dd className="font-semibold">{t("dailyValue")}</dd>
                    </div>
                    <div className="flex items-baseline justify-between gap-3">
                      <dt className="opacity-70">{t("capLabel")}</dt>
                      <dd className="font-semibold">{t("capValue")}</dd>
                    </div>
                    {supply.nextCap !== null && (
                      <div className="flex items-baseline justify-between gap-3">
                        <dt className="opacity-70">{t("nextCapLabel")}</dt>
                        <dd className="font-semibold text-[#8a5a1c]">{t("nextCapValue")}</dd>
                      </div>
                    )}
                  </dl>
                  <p className="mt-1.5 text-xs leading-relaxed opacity-65">
                    {full ? t("full") : t("untilNext")}
                    {freePoints > supply.cap && t("overCap")}
                  </p>
                </div>

                {/* レベルの進み（カードの下の段）。冊数は「あと何冊で」の一つだけにする（今のレベルの中の 5/20冊 は、読み終えた本の冊数と食い違って見えるので出さない） */}
                <div className="mt-auto flex items-end justify-between gap-3 pt-2">
                  <div className="min-w-0 flex-1">
                    <p className="mb-1 text-sm">
                      {progress ? (
                        <>
                          {bigBefore}
                          {bigAfter !== null && <><span className="mx-1 font-mincho text-2xl font-bold leading-none text-[#8a5a1c]">{left}冊</span>{bigAfter}</>}
                        </>
                      ) : (
                        t("maxLevel")
                      )}
                    </p>
                    <Gauge ratio={ratio} className="h-2" track="bg-[#2b2219]/12 shadow-[inset_0_1px_1px_#2b221922]" bar="from-[#d9b56f] to-[#9c7638]" />
                  </div>
                  <button type="button" tabIndex={flipped ? -1 : 0} className="shrink-0 text-[11px] text-[#2b2219]/65 underline decoration-dotted underline-offset-2">
                    {t("flipToBack")} ↻
                  </button>
                </div>
              </div>
            </div>

            {/* 裏: これまでの記録。名前と数字の行にそろえる */}
            <div className={`${CARD_FACE} [transform:rotateY(180deg)]`} style={CARD_PAPER} aria-hidden={!flipped}>
              {/* 磁気の帯 */}
              <div className="mt-3 h-7 bg-gradient-to-b from-[#2a241d] to-[#16120e]" aria-hidden="true" />
              <div className="flex flex-1 flex-col px-4 pb-3 pt-2.5 text-sm">
                <p className="mb-1.5 font-mincho font-semibold">{t("recordsHeading")}</p>
                <dl className="flex flex-col gap-1">
                  {recordRows.map((row) => (
                    <div key={row.id} className="flex items-baseline justify-between gap-3">
                      <dt className="opacity-70">{row.label}</dt>
                      <dd className="font-semibold">{row.value}</dd>
                    </div>
                  ))}
                </dl>
                {t("readsNote") && (
                  <p className="mt-2 border-t border-[#2b2219]/15 pt-1.5 text-xs leading-relaxed opacity-65">
                    {t("readsNote")}
                  </p>
                )}
                <div className="mt-auto flex justify-end pt-1.5">
                  <button type="button" tabIndex={flipped ? 0 : -1} className="text-[11px] text-[#2b2219]/65 underline decoration-dotted underline-offset-2">
                    {t("flipToFront")} ↻
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* 押すと開くカードを小さくしたもの（生成りの紙・左に紺の帯と金の本の紋）。
          スマホでは globals.css の .library-location の決まりが上下の余白を詰める */}
      <button
        ref={buttonRef}
        type="button"
        onClick={() => {
          setFlipped(false);
          setOpen((v) => !v);
        }}
        aria-expanded={open}
        className="relative w-full overflow-hidden rounded-[10px] py-2 pl-9 pr-3 text-left text-[#2b2219] shadow-md shadow-black/40 ring-1 ring-inset ring-[#b8924f]/50 transition-all hover:brightness-105 active:scale-[0.98]"
        style={CARD_PAPER}
      >
        <span
          aria-hidden="true"
          className="absolute inset-y-0 left-0 flex w-6 items-center justify-center bg-gradient-to-b from-[#2b3a58] to-[#151d31] shadow-[inset_-1px_0_0_#b8924f80]"
        >
          <LibrarySeal text="" emblem="book" size={16} />
        </span>
        {/* 左にレベルとゲージ、細い線をはさんで右に魔力（横長のカード・10/12 本人「魔力をカードのボタンに取り込んで横長に」） */}
        <span className="flex items-center gap-3">
          <span className="min-w-0 flex-1">
            <span className="flex items-baseline justify-between gap-1">
              <span className="font-mincho text-sm font-bold text-[#1d2a45]">{t("badgeLevel")}</span>
              {/* 冊数はカードと同じ「あと何冊で」だけ（今のレベルの中の 5/20冊 は、読み終えた本の冊数と食い違って見えるので出さない） */}
              <span className="text-[11px] opacity-70">{progress ? t("badgeNext") : t("badgeMax")}</span>
            </span>
            <Gauge ratio={ratio} className="mt-1 h-1.5" track="bg-[#2b2219]/12 shadow-[inset_0_1px_1px_#2b221922]" bar="from-[#d9b56f] to-[#9c7638]" />
          </span>
          <span aria-hidden="true" className="h-8 w-px shrink-0 bg-[#b8924f]/50" />
          <span className="shrink-0 whitespace-nowrap font-mincho text-[#1d2a45]">
            <span className="text-xs">{raw("home.magic")}</span>
            <strong className="text-lg">{(magic.free + magic.paid).toLocaleString("ja-JP")}</strong>
            {raw("home.magicUnit") && <span className="ml-0.5 text-xs">{raw("home.magicUnit")}</span>}
            {magic.paid > 0 && <span className="sr-only">{fillUiText(raw("home.magicBreakdown"), { 無償: magic.free, 有償: magic.paid })}</span>}
          </span>
        </span>
      </button>
      {open && mounted && createPortal(panel, document.body)}
    </>
  );
}
